import { FC } from "react";
import { Loader2, Lock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { RxWaivableFeeDto, WaivableFee } from "../../../../api-client";
import { useRxFeeWaivers } from "../../../../stores/Transactions/transaction.queries";
import { useSetRxFeeWaivers } from "../../../../stores/Transactions/transaction.mutations";

const fmt = (n: number) => Number(n).toFixed(2).replace(".", ",");

interface RxFeeWaiversProps {
  rxId: string;
  enabled: boolean;
}

/**
 * The automatically added fees of a private Rx (Erstbefundung PBE / Ergo ANA),
 * each switchable off for this one Rx. Charged by default.
 *
 * Renders nothing for Rxs without waivable fees. Locked while the Rx has a live
 * Rechnung: changing the fee afterwards would make the Rechnung disagree with
 * its own PDF, so the Rechnung has to be cancelled and issued anew.
 */
const RxFeeWaivers: FC<RxFeeWaiversProps> = ({ rxId, enabled }) => {
  const { data, isLoading } = useRxFeeWaivers(rxId, enabled);
  const setWaivers = useSetRxFeeWaivers();

  if (isLoading || !data || data.fees.length === 0) return null;

  const locked = !!data.lockedByRechnung;
  const busy = setWaivers.isPending;

  const toggle = (fee: RxWaivableFeeDto, charge: boolean) => {
    const waived = data.fees.filter((f) => f.waived).map((f) => f.fee);
    const next: WaivableFee[] = charge
      ? waived.filter((f) => f !== fee.fee)
      : [...waived.filter((f) => f !== fee.fee), fee.fee];
    setWaivers.mutate({ rxId, waivedFees: next });
  };

  return (
    <div className="border-b border-slate-200 bg-white px-5 py-3">
      <div className="mb-2 flex items-center gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
          Zusätzliche Gebühren
        </p>
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
      </div>

      <ul className="flex flex-col gap-1.5">
        {data.fees.map((fee) => {
          const id = `fee-waiver-${rxId}-${fee.fee}`;
          return (
            <li key={fee.fee} className="flex items-center gap-2.5">
              <Checkbox
                id={id}
                checked={!fee.waived}
                disabled={locked || busy}
                onCheckedChange={(checked) => toggle(fee, checked === true)}
              />
              <label
                htmlFor={id}
                className={`text-base ${
                  locked ? "text-slate-500" : "cursor-pointer text-slate-800"
                } ${fee.waived ? "line-through decoration-slate-400" : ""}`}
              >
                <span className="font-semibold">{fee.abbreviation}</span> – {fee.description} (
                {fmt(fee.price)} €) berechnen
              </label>
              {fee.waived && (
                <span className="text-sm font-medium text-amber-700">erlassen</span>
              )}
            </li>
          );
        })}
      </ul>

      {locked && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500">
          <Lock className="h-3.5 w-3.5" />
          Rechnung {data.lockedByRechnung} besteht bereits. Zum Ändern die Rechnung zuerst
          stornieren und danach neu erstellen.
        </p>
      )}
    </div>
  );
};

export default RxFeeWaivers;
